import { ComponentProps, FC, forwardRef, PropsWithChildren, ReactNode } from 'react'
import { styled } from '../../config'
import { Spinner } from '../Spinner'

const Root = styled('button', {
  border: 'none',
  outline: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  transition: 'all 100ms ease',
  flexShrink: 0,
  cursor: 'pointer',
  '&:disabled': {
    pointerEvents: 'none'
  },
  '& > svg': {
    flexShrink: 0
  },
  variants: {
    round: {
      true: {
        borderRadius: '$round !important'
      }
    },
    size: {
      lg: {
        width: '48px',
        height: '48px',
        borderRadius: '10px',
        '& > svg': {
          width: '24px',
          height: '24px'
        }
      },
      md: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        '& > svg': {
          width: '20px',
          height: '20px'
        }
      },
      sm: {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        '& > svg': {
          width: '20px',
          height: '20px'
        }
      },
      xs: {
        width: '20px',
        height: '20px',
        borderRadius: '3px',
        '& > svg': {
          width: '12px',
          height: '12px'
        }
      }
    },
    variant: {
      primary: {
        background: '$gray1000',
        color: '$white',
        '&:disabled': { background: '$blackA050', color: '$blackA300' },
        '&:hover': { background: '$gray0700' },
        '&:active': { background: '$gray0600' }
      },
      secondary: {
        background: 'transparent',
        borderStyle: 'solid',
        borderColor: '$blackA400',
        borderWidth: '1px',
        color: '$black',
        '&:disabled': { borderColor: '$blackA150', color: '$blackA300' },
        '&:hover': { borderColor: '$blackA700' },
        '&:active': { borderColor: '$black' }
      },
      secondaryInverted: {
        background: 'transparent',
        borderStyle: 'solid',
        borderColor: '$whiteA400',
        borderWidth: '1px',
        color: '$white',
        '&:disabled': { borderColor: '$whiteA150', color: '$whiteA300' },
        '&:hover': { borderColor: '$whiteA700' },
        '&:active': { borderColor: '$white' }
      },
      success: {
        background: '$indigo0600',
        color: '$white',
        '&:disabled': { background: '$blackA050', color: '$blackA300' },
        '&:hover': { background: '$indigo0500' },
        '&:active': { background: '$indigo0400' }
      },
      ghost: {
        background: 'transparent',
        color: '$black',
        '&:disabled': { color: '$blackA300' },
        '&:hover': { background: '$blackA050' },
        '&:active': { background: '$blackA100' }
      },
      ghostInverted: {
        background: 'transparent',
        color: '$white',
        '&:disabled': { color: '$whiteA300' },
        '&:hover': { background: '$whiteA050' },
        '&:active': { background: '$whiteA100' }
      },
      ghostSecondaryInverted: {
        background: 'transparent',
        color: '$whiteA500',
        '&:disabled': { color: '$whiteA300' },
        '&:hover': { background: '$whiteA050', color: '$whiteA800' },
        '&:active': { background: '$whiteA100', color: '$white' }
      },
      ghostInvertedLight: {
        backgroundColor: '$whiteA050',
        color: '$white',
        '&:disabled': { color: '$whiteA300' },
        '&:hover': { background: '$whiteA100' },
        '&:active': { background: '$whiteA200' }
      },
      outlineDark: {
        background: 'transparent',
        color: '$white',
        borderColor: '$white',
        borderWidth: '2px',
        borderStyle: 'solid',
        '&:hover': { background: '$whiteA100' }
      },
      ghostIndigo: {
        background: 'transparent',
        border: 'none',
        color: '$indigo0600',
        '&:disabled': { color: '$whiteA300' },
        '&:hover': { color: '$indigo0400' },
        '&:active': { color: '$indigo0300' }
      },
      dark: {
        background: '$whiteA025',
        border: '$whiteA050',
        borderWidth: '1px',
        borderStyle: 'solid',
        color: '$white',
        '&:disabled': { backgroundColor: '$whiteA0100', color: '$gray0600' },
        '&:hover': { backgroundColor: '$whiteA050' },
        '&:active': { borderColor: '$whiteA100' }
      },
      light: {
        background: '$whiteA800',
        border: '$whiteA050',
        borderWidth: '1px',
        borderStyle: 'solid',
        color: '$black',
        '&:disabled': { backgroundColor: '$whiteA0100', color: '$gray0600' },
        '&:hover': { backgroundColor: '$whiteA900' },
        '&:active': { borderColor: '$whiteA100' }
      }
    }
  },
  defaultVariants: {
    size: 'md',
    variant: 'primary'
  }
})

type ButtonProps = PropsWithChildren<
  ComponentProps<typeof Root> & {
    icon?: ReactNode
    loading?: boolean
  }
>

export const IconButton: FC<ButtonProps> = forwardRef(
  ({ icon, loading, disabled, children, ...props }, ref) => (
    <Root ref={ref} disabled={disabled || loading} {...props}>
      {loading ? <Spinner /> : icon || children}
    </Root>
  )
)
